// Renders varo.biz/{username} — what visitors see. Honors per-section
// user-customized typography and background (color, or image w/ repeat +
// position + size). YouTube videos embed inline. Outbound links open in
// new tabs. Brand icons next to social platforms.
import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { getPublicProfile } from "../api/profile";
import type { LinkOut, PublicProfileOut } from "../api/types";
import { BusinessCard, CARD_STYLES, type CardStyle, type CardUser } from "../components/BusinessCard";
import { SocialIcon } from "../components/SocialIcon";

const KNOWN_STYLES = new Set<string>(CARD_STYLES.map((s) => s.value));
function asCardStyle(v: unknown): CardStyle {
  return typeof v === "string" && KNOWN_STYLES.has(v) ? (v as CardStyle) : "editorial";
}

// Build a CSS bg dict for a styling section. Color mode → flat color.
// Image mode → image url + repeat/position/size (defaults are friendly).
function sectionBg(
  mode: string | null | undefined,
  color: string | null | undefined,
  image: string | null | undefined,
  repeat: string | null | undefined,
  position: string | null | undefined,
  size: string | null | undefined,
): CSSProperties {
  if ((mode === "bg_image" || mode === "bg_iamge") && image) {
    return {
      backgroundImage: `url(${image})`,
      backgroundColor: color ?? "transparent",
      backgroundRepeat: repeat ?? "no-repeat",
      backgroundPosition: position ?? "center",
      backgroundSize: size ?? "cover",
    };
  }
  // Default: respect color when set (even without explicit `bg_color` mode —
  // a saved hex implies "the user wants this color"). Falls back to nothing
  // so the parent section can apply its own gradient default.
  if (color) return { background: color };
  return {};
}

function youtubeEmbedUrl(url: string): string | null {
  const patterns = [
    /[?&]v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube\.com\/embed\/([\w-]{11})/,
    /youtube\.com\/shorts\/([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  return null;
}

// Ensure outbound links have a protocol — otherwise the browser treats
// "facebook.com" as a relative path and lands on /facebook.com on our domain.
// Preserves mailto: / tel: / sms: schemes; everything else gets https://.
function safeUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^[a-zA-Z][\w+.-]*:/.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function PublicProfilePage() {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<PublicProfileOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) return;
    (async () => {
      try {
        const p = await getPublicProfile(username);
        setProfile(p);
      } catch (err) {
        setError(err instanceof ApiError ? err.detail : "Profile not found");
      }
    })();
  }, [username]);

  if (error) {
    return (
      <main className="grid min-h-screen place-items-center text-center px-7">
        <div>
          <p className="eyebrow eyebrow-accent">404</p>
          <h1 className="mt-3 text-5xl tracking-tight">No card at /{username}.</h1>
          <p className="mt-5 text-ink-soft">{error}</p>
          <Link to="/" className="btn btn-secondary mt-7">
            Back to Varo
          </Link>
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className="grid min-h-[40vh] place-items-center">
        <span className="eyebrow">loading…</span>
      </main>
    );
  }

  const displayName =
    [profile.first_name, profile.last_name].filter(Boolean).join(" ") || profile.username;

  const social = profile.links.filter((l: LinkOut) => l.type === "social" && (l.link || l.value));
  const about = profile.links.filter((l: LinkOut) => l.type === "about" && (l.link || l.value));

  const heroBg = sectionBg(
    profile.color_or_img,
    profile.bg_color,
    profile.bg_image,
    profile.bg_repeat,
    profile.bg_position,
    profile.bg_size,
  );
  const cardBg = sectionBg(
    profile.card_color_or_img,
    profile.card_bg_color,
    profile.card_bg_image,
    profile.card_bg_repeat,
    profile.card_bg_position,
    profile.card_bg_size,
  );
  const videoBg = sectionBg(
    profile.vido_color_or_img,
    profile.vido_bg_color,
    profile.vido_bg_image,
    profile.vido_bg_repeat,
    profile.vido_bg_position,
    profile.vido_bg_size,
  );
  const aboutBg = sectionBg(
    profile.abt_color_or_img,
    profile.abt_bg_color,
    profile.abt_bg_image,
    profile.abt_bg_repeat,
    profile.abt_bg_position,
    profile.abt_bg_size,
  );

  // Hero default: warm dark gradient if no user customization
  const heroFinalStyle =
    Object.keys(heroBg).length > 0
      ? heroBg
      : { background: "linear-gradient(180deg, #2a201c 0%, #1a1612 100%)" };

  const videoFinalStyle =
    Object.keys(videoBg).length > 0 ? videoBg : { backgroundColor: "#1a1612" };

  return (
    <>
      <header className="flex items-center justify-between px-7 py-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute">
        <Link to="/">Set in Varo · varo.biz</Link>
        <Link to="/login" className="uline">
          Sign in →
        </Link>
      </header>

      {/* Hero — header section styling. The card_logo (brand mark) sits at
          the top per the legacy layout (`.mainlogo` div). */}
      <section className="relative overflow-hidden px-7 py-24 text-center" style={heroFinalStyle}>
        {profile.card_logo && (
          <div className="mb-9 flex justify-center">
            <img
              src={profile.card_logo}
              alt={`${displayName} logo`}
              className="max-h-20 max-w-[240px] object-contain"
              loading="lazy"
            />
          </div>
        )}
        <p className="eyebrow text-paper/55">{profile.designation ?? "Profile"}</p>
        <h1
          className="mx-auto mt-6 max-w-3xl text-6xl text-paper sm:text-7xl"
          style={{
            fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'WONK' 1, 'wght' 400",
            letterSpacing: "-0.045em",
            lineHeight: 0.95,
          }}
        >
          {displayName}
        </h1>
        {profile.about_heading && (
          <p className="mx-auto mt-7 max-w-2xl text-lg text-paper/70">{profile.about_heading}</p>
        )}
      </section>

      {/* Card section — outer container honors user bg customization; the
          inner card uses the selected design preset from `card_style`. */}
      <section className="px-7 py-16" style={cardBg}>
        <div className="mx-auto max-w-4xl">
          <p className="eyebrow eyebrow-accent mb-7">
            <span className="mr-3 font-display italic text-terra">03</span> The card
          </p>
          <div className="flex justify-center">
            <BusinessCard
              variant={asCardStyle(profile.card_style)}
              user={profile as unknown as CardUser}
              size="full"
            />
          </div>
        </div>
      </section>

      {/* Video section */}
      {profile.video_url && (
        <section className="px-7 py-16 text-paper" style={videoFinalStyle}>
          <div className="mx-auto max-w-4xl grid gap-9 md:grid-cols-2 items-center">
            <div>
              <p className="eyebrow text-terra mb-5">
                <span className="mr-3 font-display italic">04</span> Watch
              </p>
              <h2
                className="text-4xl"
                style={{ fontVariationSettings: "'opsz' 96, 'SOFT' 30, 'wght' 400" }}
              >
                {profile.video_heading ?? "Featured"}
              </h2>
            </div>
            {(() => {
              const embed = youtubeEmbedUrl(profile.video_url!);
              return embed ? (
                <iframe
                  src={embed}
                  title={profile.video_heading ?? "Video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="aspect-video w-full rounded-xl border-0 shadow-2xl"
                />
              ) : (
                <a
                  href={profile.video_url!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="grid aspect-video place-items-center rounded-xl bg-gradient-to-br from-terra to-terra-deep transition-transform hover:scale-[1.01]"
                >
                  <span className="size-20 rounded-full bg-paper/95 grid place-items-center text-2xl text-ink">
                    ▶
                  </span>
                </a>
              );
            })()}
          </div>
        </section>
      )}

      {/* About section */}
      {profile.about_text && (
        <section className="px-7 py-20" style={aboutBg}>
          <div className="mx-auto max-w-2xl">
            <p className="eyebrow eyebrow-accent mb-7">
              <span className="mr-3 font-display italic text-terra">05</span> About
            </p>
            <div
              className="font-display text-2xl leading-relaxed text-ink"
              style={{ fontVariationSettings: "'opsz' 36, 'SOFT' 60, 'wght' 350" }}
            >
              <p>{profile.about_text}</p>
            </div>
            {about.length > 0 && (
              <dl className="mt-9 grid grid-cols-[16ch_1fr] gap-3 border-t border-rule pt-6">
                {about.map((row) => {
                  const safe = safeUrl(row.link);
                  return (
                    <div key={row.id} className="contents">
                      <dt className="eyebrow">{row.value ?? "Note"}</dt>
                      <dd className="text-sm">
                        {safe ? (
                          <a
                            className="uline"
                            href={safe}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.link}
                          </a>
                        ) : (
                          row.value
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}
          </div>
        </section>
      )}

      {/* Social footer */}
      {social.length > 0 && (
        <footer className="border-t border-rule bg-cream-card px-7 py-12">
          <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3">
            <ul className="flex flex-wrap gap-3">
              {social.map((s) => {
                const safe = safeUrl(s.link);
                return safe ? (
                  <li key={s.id}>
                    <a
                      className="inline-flex items-center gap-2 rounded-full border border-rule bg-paper px-4 py-2 text-sm hover:bg-ink hover:text-paper transition-colors"
                      href={safe}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SocialIcon platform={s.value} />
                      {s.value ?? "Link"}
                    </a>
                  </li>
                ) : null;
              })}
            </ul>
            <span className="font-display italic text-terra-deep">— set in Varo.</span>
          </div>
        </footer>
      )}
    </>
  );
}
